import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon, Input, Skeleton, Tooltip } from '@sendible/design-system';
import { IconNames } from '@sendible/design-system/src/theme';
import { useTranslation } from 'react-i18next';
import {
  Container as SidebarContainer,
  IconRail as SidebarIconRail,
  IconButton as SidebarIconButton,
  PopoverHeader as SidebarPopoverHeader,
  PopoverPanel as SidebarPopoverPanel,
  PopoverTitleBar,
  PopoverCloseButton,
  FilterTabsRow,
  FilterTabButton,
  ChartTypeRow,
  ChartTypeIconButton,
  ChartTypeSectionLabel,
  NetworkFilterRow,
  NetworkFilterButton,
  SearchWrapper as SidebarSearchWrapper,
  ModuleDropList,
  ModuleDropItem,
  ModuleItemLabel,
  ModulePlatformBadges,
  ModuleLabelGroup,
  ModuleAvatar,
  SkeletonWrapper,
  ModuleDropItemDisabled,
} from './index.styles';
import { MODULE_CODE_TO_TRANSLATION_KEYS, normalisePlatform } from '../../../../models/reporting/transforms';
import { useGetModuleDefinitions } from '../../../../models/reporting';

// ── Platform badge icons ──
const PLATFORM_BADGE_ICON_MAP: Record<string, IconNames> = {
  Facebook: 'facebook_color',
  FacebookPage: 'facebook_color',
  TikTok: 'tiktok_color',
  Instagram: 'instagram_color',
  InstagramBusiness: 'instagram_color',
  X: 'x_color',
  LinkedIn: 'linkedin_color',
  LinkedInCompanyPage: 'linkedin_color',
  LinkedInProfile: 'linkedin_color',
};

// ── Chart type icon/label mapping (ordered as shown in the design) ──

const CHART_TYPE_ORDER = ['MetricCard', 'Line', 'Area', 'Pie', 'Bar', 'SummaryTable', 'List'];

const CHART_TYPE_ICONS: Record<string, IconNames> = {
  MetricCard: 'hashtag',
  Line: 'activity',
  Area: 'area_chart',
  Pie: 'pie_chart',
  Bar: 'bar_chart',
  SummaryTable: 'table',
  List: 'list_dashes',
};

const CHART_TYPE_LABEL_KEYS: Record<string, string> = {
  MetricCard: 'new_reports_chart_type_metric_card',
  Line: 'new_reports_chart_type_line',
  Area: 'new_reports_chart_type_area',
  Bar: 'new_reports_chart_type_bar',
  Pie: 'new_reports_chart_type_pie',
  SummaryTable: 'new_reports_chart_type_table',
  List: 'new_reports_chart_type_list',
};

type NetworkFilter = 'all' | 'Facebook' | 'TikTok';

const NETWORK_FILTERS: { id: NetworkFilter; icon: IconNames; labelKey: string }[] = [
  { id: 'all', icon: 'grid', labelKey: 'new_reports_network_all' },
  { id: 'Facebook', icon: 'facebook_color', labelKey: 'new_reports_network_facebook' },
  { id: 'TikTok', icon: 'tiktok_color', labelKey: 'new_reports_network_tiktok' },
];

const MODULE_ICON_MAP: Record<string, IconNames> = {
  'Follower Growth': 'users',
  'Audience Demographics': 'globe',
  'Top Posts': 'grid',
  'TikTok Audience': 'users',
  'TikTok Top Videos': 'play_circle',
  'Facebook Page Insights': 'activity',
  'Facebook Top Posts': 'grid',
};

type AddModuleTab = 'all' | 'network' | 'charts';

type SidebarItem = {
  definitionId: number;
  label: string;
  iconName: IconNames;
  description?: string;
  platforms: string[];
  chartTypes: string[];
};

type SidebarPanel = 'add' | 'modules' | null;

type EditorSidebarProps = {
  currentModules: { reportModuleId: string; moduleType: ModuleType; customName: string | null }[];
  onAddModule?: (definitionId: number) => void;
  onScrollToModule?: (reportModuleId: string) => void;
  disabled?: boolean;
  triggerOpenAddPanel?: number;
};

export const EditorSidebar = ({ currentModules, onAddModule, onScrollToModule, disabled, triggerOpenAddPanel }: EditorSidebarProps) => {
  const { t } = useTranslation('reports');
  const [activePanel, setActivePanel] = useState<SidebarPanel>(null);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<AddModuleTab>('all');
  const [activeChartType, setActiveChartType] = useState<string | null>(null);
  const [activeNetwork, setActiveNetwork] = useState<NetworkFilter>('all');
  const sidebarRef = useRef<HTMLDivElement>(null);

  // ── Render-Phase Synchronization ──
  // Track previous panel trigger key to sync state instantly during render pass
  const [prevTrigger, setPrevTrigger] = useState<number | undefined>(triggerOpenAddPanel);

  if (triggerOpenAddPanel !== prevTrigger) {
    setPrevTrigger(triggerOpenAddPanel);
    setActivePanel('add');
  }

  // Fetch module definitions from the API
  const { data: moduleDefinitions, isLoading: isLoadingDefinitions } = useGetModuleDefinitions();

  // Build flat item list from API data
  const allItems = useMemo<SidebarItem[]>(() => {
    if (!moduleDefinitions || moduleDefinitions.length === 0) return [];

    return moduleDefinitions.map((def) => {
      const moduleCodeKeys = def.moduleCode ? MODULE_CODE_TO_TRANSLATION_KEYS[def.moduleCode] : null;
      const label = moduleCodeKeys ? t(moduleCodeKeys.titleKey) : def.name;
      const resolvedDescription = moduleCodeKeys ? (t(moduleCodeKeys.descriptionKey) as string) : def.description;

      return {
        definitionId: def.id,
        label,
        iconName: MODULE_ICON_MAP[def.moduleCode ?? def.name] ?? MODULE_ICON_MAP[def.name] ?? 'bar_chart',
        description: resolvedDescription ?? undefined,
        platforms: def.supportedProfileTypes ?? [],
        chartTypes: def.supportedChartTypes ?? [],
      };
    });
  }, [moduleDefinitions, t]);

  // Derive chart types present in the data, in design order
  const availableChartTypes = useMemo(() => {
    const presentTypes = new Set(allItems.flatMap((item) => item.chartTypes));

    return CHART_TYPE_ORDER.filter((ct) => presentTypes.has(ct));
  }, [allItems]);

  // Filtered items based on active tab, network/chart filters, and search
  const filteredItems = useMemo(() => {
    let items = allItems;

    if (activeTab === 'network' && activeNetwork !== 'all') {
      items = items.filter((item) => item.platforms.some((p) => normalisePlatform(p) === activeNetwork));
    }

    if (activeTab === 'charts' && activeChartType) {
      items = items.filter((item) => item.chartTypes.includes(activeChartType));
    }

    if (search.trim()) {
      const q = search.toLowerCase();

      items = items.filter((item) => item.label.toLowerCase().includes(q) || item.description?.toLowerCase().includes(q));
    }

    return items;
  }, [allItems, activeTab, activeNetwork, activeChartType, search]);

  const togglePanel = useCallback((panel: SidebarPanel) => {
    setActivePanel((prev) => (prev === panel ? null : panel));
    setSearch('');
  }, []);

  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      if (e.relatedTarget && sidebarRef.current && !sidebarRef.current.contains(e.relatedTarget as Node)) {
        setActivePanel(null);
      }
    },
    [sidebarRef]
  );

  useEffect(() => {
    if (!activePanel) return undefined;

    const handleMouseDown = (e: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target as Node)) {
        setActivePanel(null);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);

    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [activePanel]);

  // Handle Tab Switch Actions via pure callback handlers rather than layout loops
  const handleTabChange = (tab: AddModuleTab) => {
    setActiveTab(tab);
    if (tab === 'charts' && availableChartTypes.length > 0) {
      setActiveChartType(availableChartTypes[0]);
    }
  };

  const networkSectionLabel = activeNetwork === 'all' ? t('new_reports_cross_network') : activeNetwork;

  // Fallback to auto-select first option if tab is chart and selection state is blanked out
  const effectiveChartType = activeTab === 'charts' && !activeChartType && availableChartTypes.length > 0 ? availableChartTypes[0] : activeChartType;

  // Module info lookup for the current-modules panel
  const getModuleInfo = (moduleType: ModuleType): { label: string; iconName: IconNames } => {
    const entry = allItems.find((item) => item.label.toLowerCase().replace(/\s+/g, '_') === moduleType);

    return {
      label: entry?.label ?? moduleType.replace(/_/g, ' '),
      iconName: entry?.iconName ?? 'bar_chart',
    };
  };

  return (
    <SidebarContainer
      ref={sidebarRef}
      onBlur={handleBlur}
      style={disabled ? { pointerEvents: 'none', opacity: 0.5 } : undefined}
      data-testid="editor-sidebar-container"
    >
      <SidebarIconRail>
        {/* ── Add module ── */}
        <Tooltip
          content={t('new_reports_add_module')}
          noDecoration
          defaultPosition="right"
        >
          <SidebarIconButton
            type="button"
            $isActive={activePanel === 'add'}
            $isPrimary
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              togglePanel('add');
            }}
            onMouseDown={(e) => {
              e.preventDefault();
            }}
            aria-label={t('new_reports_add_module')}
          >
            <Icon name="plus" />
          </SidebarIconButton>
        </Tooltip>

        {/* ── View modules ── */}
        <Tooltip
          content={t('new_reports_modules')}
          noDecoration
          defaultPosition="right"
        >
          <SidebarIconButton
            type="button"
            $isActive={activePanel === 'modules'}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              togglePanel('modules');
            }}
            onMouseDown={(e) => {
              e.preventDefault();
            }}
            aria-label={t('new_reports_view_modules')}
          >
            <Icon
              color="brandDark"
              name="list_dashes"
            />
          </SidebarIconButton>
        </Tooltip>
      </SidebarIconRail>

      {/* ── Add module popover ── */}
      {activePanel === 'add' && (
        <SidebarPopoverPanel>
          <PopoverTitleBar>
            <span>{t('new_reports_add_module')}</span>
            <PopoverCloseButton
              type="button"
              aria-label={t('new_reports_close_menu', { defaultValue: 'Close menu' })}
              onClick={() => setActivePanel(null)}
            >
              <Icon
                name="close"
                color="brandDark"
              />
            </PopoverCloseButton>
          </PopoverTitleBar>

          <SidebarSearchWrapper>
            <Input
              icon="search"
              size="sm"
              label={t('new_reports_search_modules')}
              value={search}
              change={(val: string) => setSearch(val)}
            />
          </SidebarSearchWrapper>

          <FilterTabsRow>
            <FilterTabButton
              type="button"
              $isActive={activeTab === 'all'}
              onClick={() => handleTabChange('all')}
            >
              {t('new_reports_filter_all')}
            </FilterTabButton>
            <FilterTabButton
              type="button"
              $isActive={activeTab === 'network'}
              onClick={() => handleTabChange('network')}
            >
              {t('new_reports_filter_network')}
            </FilterTabButton>
            <FilterTabButton
              type="button"
              $isActive={activeTab === 'charts'}
              onClick={() => handleTabChange('charts')}
            >
              {t('new_reports_filter_charts')}
            </FilterTabButton>
          </FilterTabsRow>

          {activeTab === 'network' && !isLoadingDefinitions && (
            <NetworkFilterRow>
              {NETWORK_FILTERS.map(({ id, icon, labelKey }) => (
                <Tooltip
                  key={id}
                  content={t(labelKey)}
                  noDecoration
                  defaultPosition="bottom"
                >
                  <NetworkFilterButton
                    type="button"
                    $isActive={activeNetwork === id}
                    aria-label={t(labelKey) as string}
                    onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                    onClick={() => setActiveNetwork(id)}
                  >
                    <Icon
                      color="brandDark"
                      name={icon}
                    />
                  </NetworkFilterButton>
                </Tooltip>
              ))}
            </NetworkFilterRow>
          )}

          {activeTab === 'network' && !isLoadingDefinitions && <ChartTypeSectionLabel>{networkSectionLabel}</ChartTypeSectionLabel>}

          {activeTab === 'charts' && !isLoadingDefinitions && availableChartTypes.length > 0 && (
            <ChartTypeRow>
              {availableChartTypes.map((chartType) => (
                <Tooltip
                  key={chartType}
                  content={t(CHART_TYPE_LABEL_KEYS[chartType] ?? chartType)}
                  noDecoration
                  defaultPosition="bottom"
                >
                  <ChartTypeIconButton
                    type="button"
                    $isActive={effectiveChartType === chartType}
                    aria-label={t(CHART_TYPE_LABEL_KEYS[chartType] ?? chartType) as string}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setActiveChartType((prev) => (prev === chartType ? null : chartType))}
                  >
                    <Icon
                      name={CHART_TYPE_ICONS[chartType] ?? 'bar_chart'}
                      color={effectiveChartType === chartType ? 'brandPrimary' : 'brandDarkTint30'}
                    />
                  </ChartTypeIconButton>
                </Tooltip>
              ))}
            </ChartTypeRow>
          )}

          {activeTab === 'charts' && !isLoadingDefinitions && (
            <ChartTypeSectionLabel>
              {effectiveChartType ? t(CHART_TYPE_LABEL_KEYS[effectiveChartType]) : t('new_reports_chart_type_metric_card')}
            </ChartTypeSectionLabel>
          )}

          {isLoadingDefinitions ? (
            <SkeletonWrapper>
              <Skeleton
                width="100%"
                height="2.5rem"
              />
              <Skeleton
                width="100%"
                height="2.5rem"
              />
              <Skeleton
                width="100%"
                height="2.5rem"
              />
              <Skeleton
                width="100%"
                height="2.5rem"
              />
            </SkeletonWrapper>
          ) : (
            <ModuleDropList>
              {filteredItems.length === 0 ? (
                <ModuleDropItemDisabled>
                  <ModuleItemLabel>{t('new_reports_no_modules_available')}</ModuleItemLabel>
                </ModuleDropItemDisabled>
              ) : (
                filteredItems.map((entry) => (
                  <ModuleDropItem
                    key={entry.definitionId}
                    onClick={() => {
                      onAddModule?.(entry.definitionId);
                      setActivePanel(null);
                    }}
                  >
                    <ModuleItemLabel>{entry.label}</ModuleItemLabel>
                    <ModulePlatformBadges>
                      {Array.from(new Set(entry.platforms.map((platform) => PLATFORM_BADGE_ICON_MAP[platform]).filter(Boolean))).map((icon) => (
                        <Icon
                          key={icon}
                          name={icon as IconNames}
                        />
                      ))}
                    </ModulePlatformBadges>
                  </ModuleDropItem>
                ))
              )}
            </ModuleDropList>
          )}
        </SidebarPopoverPanel>
      )}

      {/* ── Current modules popover ── */}
      {activePanel === 'modules' && (
        <SidebarPopoverPanel>
          <SidebarPopoverHeader>{t('new_reports_modules_in_report', { count: currentModules.length })}</SidebarPopoverHeader>
          <ModuleDropList>
            {currentModules.length === 0 ? (
              <ModuleDropItemDisabled>
                <ModuleItemLabel>{t('new_reports_no_modules_added')}</ModuleItemLabel>
              </ModuleDropItemDisabled>
            ) : (
              currentModules.map((mod, idx) => {
                const { label, iconName } = getModuleInfo(mod.moduleType);

                return (
                  <ModuleDropItem
                    key={mod.reportModuleId}
                    onClick={() => onScrollToModule?.(mod.reportModuleId)}
                  >
                    <ModuleLabelGroup>
                      <ModuleAvatar>
                        <Icon name={iconName} />
                      </ModuleAvatar>
                      <ModuleItemLabel>
                        {idx + 1}. {mod.customName ?? label}
                      </ModuleItemLabel>
                    </ModuleLabelGroup>
                  </ModuleDropItem>
                );
              })
            )}
          </ModuleDropList>
        </SidebarPopoverPanel>
      )}
    </SidebarContainer>
  );
};
